import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, fmtDateTime } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CalendarClock, Landmark, ShieldOff, CheckCircle2, XCircle, Clock, Eye } from "lucide-react";
import { SectionBody, SectionHead } from "@/components/SectionCollapse";

const MailPreview = ({ orderId }) => {
  const [mail, setMail] = useState(null);
  const open = async (key) => {
    try { const r = await api.get(`/auftraege/${orderId}/zahlungsfrist/vorschau/${key}`); setMail(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Vorschau fehlgeschlagen."); }
  };
  return (
    <>
      <Button size="sm" variant="ghost" onClick={() => open("zahlungsfrist_erinnerung")} data-testid="zf-preview-reminder"><Eye className="w-3.5 h-3.5 mr-1" /> Vorschau Erinnerung</Button>
      <Button size="sm" variant="ghost" onClick={() => open("zahlungsfrist_abgelaufen")} data-testid="zf-preview-expired"><Eye className="w-3.5 h-3.5 mr-1" /> Vorschau Ablauf-Mail</Button>
      <Dialog open={!!mail} onOpenChange={(v) => !v && setMail(null)}>
        <DialogContent className="max-w-3xl" data-testid="zf-preview-dialog">
          <DialogHeader><DialogTitle className="text-base">{mail?.subject}</DialogTitle></DialogHeader>
          <iframe title="Vorschau" className="w-full h-[60vh] border rounded" srcDoc={mail?.html_body || ""} />
        </DialogContent>
      </Dialog>
    </>
  );
};

const daysLeft = (iso) => Math.ceil((new Date(iso) - new Date()) / 86400000);
const MODE = { inherit: null, ja: true, nein: false };
const modeOf = (v) => (v === true ? "ja" : v === false ? "nein" : "inherit");

const TransferActions = ({ order, onChange, canAct }) => {
  const [busy, setBusy] = useState("");
  const act = async (aktion, msg) => {
    setBusy(aktion);
    try { await api.post(`/auftraege/${order.id}/ueberweisung/${aktion}`); toast.success(msg); await onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Aktion fehlgeschlagen."); }
    finally { setBusy(""); }
  };
  return (
    <div className="rounded-md border border-sky-200 bg-sky-50 p-3 space-y-2" data-testid="zf-transfer">
      <div className="flex items-center gap-2 text-sm text-sky-900"><Landmark className="w-4 h-4" />
        <span>Kunde hat Überweisung gewählt{order.zahlart_gewaehlt_am ? ` am ${fmtDateTime(order.zahlart_gewaehlt_am)}` : ""} – keine automatische Stornierung.</span>
      </div>
      {order.ueberweisung_rueckfrage_am && <div className="text-xs text-sky-800" data-testid="zf-transfer-next">Nächste Rückfrage: {fmtDateTime(order.ueberweisung_rueckfrage_am)}</div>}
      {order.ueberweisung_task_offen && <div className="text-xs font-semibold text-sky-900" data-testid="zf-transfer-question">Ist die Überweisung eingegangen?</div>}
      {canAct && (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={!!busy} onClick={() => act("eingegangen", "Zahlung als eingegangen markiert.")} data-testid="zf-transfer-paid"><CheckCircle2 className="w-4 h-4 mr-1" /> Zahlung eingegangen</Button>
          <Button size="sm" variant="outline" className="border-rose-300 text-rose-700" disabled={!!busy} onClick={() => act("stornieren", "Auftrag storniert, Kunde informiert.")} data-testid="zf-transfer-cancel"><XCircle className="w-4 h-4 mr-1" /> Nicht eingegangen – stornieren</Button>
          {order.ueberweisung_task_offen && <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => act("morgen", "Rückfrage kommt morgen wieder.")} data-testid="zf-transfer-snooze"><Clock className="w-4 h-4 mr-1" /> Morgen erneut fragen</Button>}
        </div>
      )}
    </div>
  );
};

export const ZahlungsfristBox = ({ order, onChange, canEdit, collapseKey }) => {
  const waiting = order.status === "wartet_auf_zahlung";
  const autoCancelled = order.status === "storniert" && ["auto", "ueberweisung_nicht_eingegangen"].includes(order.storno_art);
  if (order.kostenlos) return null; // 0-€-Auftrag: keine Zahlungsfrist, keine Erinnerung, kein Auto-Storno
  if (!waiting && !autoCancelled) return null;
  const setMode = async (m) => {
    try { await api.put(`/auftraege/${order.id}/auto-storno`, { ausschluss: MODE[m] }); toast.success("Einstellung gespeichert."); await onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen."); }
  };
  const left = order.zahlungsfrist_bis ? daysLeft(order.zahlungsfrist_bis) : null;
  return (
    <Card className="border-slate-200" data-testid="zahlungsfrist-box">
      <CardContent className="p-4 space-y-3">
        <SectionHead k={waiting && order.zahlart_gewaehlt === "ueberweisung" ? collapseKey : undefined} label="Zahlungsfrist" className="flex flex-wrap items-center gap-3 text-sm">
          <CalendarClock className="w-4 h-4 text-slate-500" />
          {autoCancelled ? (
            <span data-testid="zf-auto-cancelled"><b>{order.storno_grund}</b>{order.reaktivierbar_bis ? ` · Kunde kann bis ${fmtDateTime(order.reaktivierbar_bis)} selbst reaktivieren` : ""}</span>
          ) : order.auto_storno_ausgeschlossen ? (
            <span className="inline-flex items-center gap-1 text-slate-700" data-testid="zf-excluded"><ShieldOff className="w-4 h-4" /> Keine automatische Stornierung</span>
          ) : order.zahlungsfrist_bis && order.zahlart_gewaehlt !== "ueberweisung" ? (
            <span data-testid="zf-deadline">Zahlungsfrist bis <b>{fmtDateTime(order.zahlungsfrist_bis)}</b> <span className={left <= 2 ? "text-rose-700 font-semibold" : "text-slate-500"}>({left > 0 ? `noch ${left} ${left === 1 ? "Tag" : "Tage"}` : "abgelaufen"})</span></span>
          ) : null}
          {order.erinnerung_gesendet_am && waiting && <span className="text-xs text-slate-500" data-testid="zf-reminder-sent">Erinnerung gesendet {fmtDateTime(order.erinnerung_gesendet_am)}</span>}
          {waiting && !order.angebot_gesendet_at && order.zahlart_gewaehlt !== "ueberweisung" && <span className="text-xs text-slate-500" data-testid="zf-not-sent">Angebot noch nicht an den Kunden versendet{order.angebot_test_mode ? " (nur Testmail)" : ""} – Frist startet mit dem ersten echten Versand</span>}
          <MailPreview orderId={order.id} />
          {waiting && canEdit && (
            <div className="ml-auto flex items-center gap-2">
              <span className="text-xs text-slate-500">Automatische Stornierung:</span>
              <Select value={modeOf(order.auto_storno_ausschluss)} onValueChange={setMode}>
                <SelectTrigger className="h-8 w-[260px]" data-testid="zf-exclusion-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="inherit">Wie beim Kunden</SelectItem>
                  <SelectItem value="ja">Nicht automatisch stornieren</SelectItem>
                  <SelectItem value="nein">Automatisch stornieren</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </SectionHead>
        {waiting && order.zahlart_gewaehlt === "ueberweisung" && <SectionBody k={collapseKey}><TransferActions order={order} onChange={onChange} canAct={canEdit} /></SectionBody>}
      </CardContent>
    </Card>
  );
};
